/**
 * Pure orbit-control / camera-clip tuning math for the Giro3D adapter.
 *
 * Extracted from `Giro3DAdapter.tuneControlsForModel` so the zoom-distance and
 * near/far decisions can be unit-tested by *scene shape* (object-only vs.
 * object + terrain/basemap, projected vs. custom frame) without standing up a
 * full Giro3D instance. No layer-id awareness lives here — only generic,
 * layer-kind/scene-shape inputs.
 */

/** Web-Mercator world half-width in metres (EPSG:3857 axis bound). */
export const WEB_MERCATOR_WORLD_HALF_WIDTH = 20_037_508.34;

export interface OrbitClampInput {
  /** Bounding-sphere radius of the mounted instance-object model, in frame units. */
  modelRadius: number;
  /** Active render-frame CRS (e.g. `'EPSG:3857'` or a custom displayCrs), or null. */
  frameCrs: string | null;
  /**
   * Whether any terrain/basemap/raster map-layer is mounted. Map-layers span far
   * beyond a single 3D object, so their presence is what makes a tight,
   * object-radius-scaled zoom-out cap wrong.
   */
  hasMapLayers: boolean;
}

export interface OrbitClamp {
  minDistance: number;
  maxDistance: number;
  minNearPlane: number;
  maxFarPlane: number;
}

/**
 * Largest sensible orbit distance for the *whole* scene rather than just the
 * model. Returns `null` when only instance-objects are mounted (keep the tight,
 * model-scaled cap). When terrain/basemap/raster map-layers are present the
 * scene is effectively region/world scale, so the camera may zoom out to the
 * frame's full extent instead of being trapped by a small object's radius —
 * without going to infinity.
 */
export function computeSceneZoomOutCap(input: {
  modelRadius: number;
  frameCrs: string | null;
  hasMapLayers: boolean;
}): number | null {
  if (!input.hasMapLayers) return null;
  if (input.frameCrs === 'EPSG:3857') {
    // Frames a whole region/continent while still bounding the camera inside
    // the projected world.
    return WEB_MERCATOR_WORLD_HALF_WIDTH;
  }
  // Custom displayCrs frame (no Web-Mercator basemap): no reliable absolute
  // metre scale, so use a generous multiple of the model radius so terrain in
  // that frame stays reachable without an unsafe unit assumption.
  return input.modelRadius > 0 ? input.modelRadius * 5000 : null;
}

/**
 * Compute orbit min/max distance and camera near/far clamps from the model
 * radius and scene shape. Returns `null` for a non-finite / non-positive radius
 * (caller should leave existing clamps untouched).
 *
 * Invariant: `maxFarPlane` always exceeds `maxDistance` so zooming out to the
 * widened scene cap never pushes the camera past the far plane (which would
 * clip the whole scene to black). Log-depth makes the large ratio harmless.
 */
export function computeOrbitClamp(input: OrbitClampInput): OrbitClamp | null {
  const r = input.modelRadius;
  if (!Number.isFinite(r) || r <= 0) return null;

  const objectCap = r * 200;
  const sceneCap = computeSceneZoomOutCap(input);
  const maxDistance = sceneCap != null ? Math.max(objectCap, sceneCap) : objectCap;

  return {
    minDistance: Math.max(r * 0.0005, 0.0001),
    maxDistance,
    minNearPlane: Math.max(r * 0.00001, 0.001),
    maxFarPlane: Math.max(r * 100000, 1000, maxDistance * 4),
  };
}
